// Resolves which backend the frontend should talk to.
//
// White-labeled deployments run the FE on a client domain (e.g. app.client.com)
// while the backend is shared. To make auth cookies first-party (so mobile
// browsers don't block them), each client points carbonapi.<their-domain> at our
// backend, and the FE sends all API requests there. Gaia-owned / local hosts are
// unchanged — they keep using the build-time REACT_APP_API_URL.
//
// We use the `carbonapi.` prefix (not `api.`) so we never collide with a service
// the client may already run at api.client.com.

// Hosts that are Gaia-owned / dev — keep the build-time API URL.
const GAIA_HOST_SUFFIXES = ['gaiacompany.io', 'amplifyapp.com'];

export function isGaiaOrLocalHost(hostname: string = window.location.hostname): boolean {
  return hostname === 'localhost'
    || hostname.endsWith('.localhost')
    || GAIA_HOST_SUFFIXES.some(s => hostname === s || hostname.endsWith(`.${s}`));
}

// Common multi-label public suffixes, so we keep three labels for these (e.g.
// democompany.co.uk) instead of collapsing to the suffix (co.uk). Extend if a
// client uses a registrable domain under a suffix not listed here.
const MULTI_LABEL_SUFFIXES = new Set([
  'co.uk', 'org.uk', 'me.uk', 'ac.uk', 'gov.uk', 'ltd.uk', 'plc.uk', 'net.uk', 'sch.uk', 'nhs.uk',
  'com.au', 'net.au', 'org.au', 'edu.au', 'gov.au',
  'co.nz', 'net.nz', 'org.nz',
  'co.za', 'org.za',
  'com.br', 'com.mx', 'com.ar', 'com.sg', 'com.my', 'com.tr', 'com.hk', 'com.cn',
  'co.in', 'co.jp', 'co.kr', 'co.id', 'co.il',
]);

/**
 * Reduce a host to its registrable (base) domain, dropping any subdomain(s):
 *   carbonaccounting.democompany.com  ->  democompany.com
 *   app.client.co.uk                  ->  client.co.uk
 *   client.com                        ->  client.com
 */
export function getRegistrableDomain(hostname: string): string {
  const labels = hostname.toLowerCase().split('.').filter(Boolean);
  if (labels.length <= 2) return labels.join('.');
  const lastTwo = labels.slice(-2).join('.');
  const keep = MULTI_LABEL_SUFFIXES.has(lastTwo) ? 3 : 2;
  return labels.slice(-keep).join('.');
}

/**
 * Derive the backend host from the current FE host by convention, stripping any
 * FE subdomain down to the registrable domain:
 *   carbonaccounting.democompany.com / app.client.com / client.com
 *     ->  carbonapi.democompany.com  /  carbonapi.client.com  /  carbonapi.client.com
 */
export function deriveApiHost(hostname: string): string {
  return `carbonapi.${getRegistrableDomain(hostname)}`;
}

/** The preferred origin (scheme + host) for the API, ignoring reachability. */
export function getApiBaseUrl(): string {
  if (isGaiaOrLocalHost()) {
    return process.env.REACT_APP_API_URL as string;
  }
  return `${window.location.protocol}//${deriveApiHost(window.location.hostname)}`;
}

const PING_PATH = '/gaia-api/v1/ping';
const PING_TIMEOUT_MS = 2500;
const API_BASE_CACHE_KEY = 'gca_api_base_url';

/** True if the API host answers the public /ping with a 2xx within the timeout. */
async function isApiReachable(origin: string): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
  try {
    const res = await fetch(`${origin}${PING_PATH}`, {
      method: 'GET',
      signal: controller.signal,
      credentials: 'omit',
      cache: 'no-store',
    });
    return res.ok;
  } catch {
    return false; // DNS not set up, bad cert, CORS not configured, timeout, etc.
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Resolve the API base URL with graceful fallback. On a white-label domain we
 * prefer the first-party carbonapi.<domain> host, but only if it actually
 * answers; otherwise we fall back to the build-time REACT_APP_API_URL (the shared
 * Gaia backend) so a client whose DNS/cert isn't ready yet can still use the app.
 * Once their first-party host is live, a fresh session picks it up automatically.
 * The decision is cached per browser session to avoid re-probing on every load.
 */
export async function resolveApiBaseUrl(): Promise<string> {
  const envUrl = process.env.REACT_APP_API_URL as string;
  if (isGaiaOrLocalHost()) return envUrl;

  try {
    const cached = sessionStorage.getItem(API_BASE_CACHE_KEY);
    if (cached) return cached;
  } catch { /* sessionStorage unavailable — fall through and probe */ }

  const candidate = `${window.location.protocol}//${deriveApiHost(window.location.hostname)}`;
  const chosen = (await isApiReachable(candidate)) ? candidate : envUrl;

  try { sessionStorage.setItem(API_BASE_CACHE_KEY, chosen); } catch { /* ignore */ }
  return chosen;
}
